/**
 * SHELL — renderer acquisition with a safe fallback.
 *
 * We call Render's locked surface `createRenderer(canvas)`. Until the Render
 * agent fills in the body it throws — so we catch and substitute a NO-OP renderer
 * that satisfies the contract, letting the full Shell (loop, input, screens) run
 * and be verified today. Swapping to the real renderer needs no call-site change:
 * the moment Render lands, `createRenderer` simply stops throwing.
 */

import type { CourseSpec, GameState } from '@/types';
import { createRenderer } from '@/render';
import type { Renderer } from '@/render';

/** A renderer that draws nothing but honors the interface. */
function createNullRenderer(): Renderer {
  return {
    buildCourse(_spec: CourseSpec): void {
      /* no-op */
    },
    render(_state: GameState, _alpha: number): void {
      /* no-op */
    },
    resize(_w: number, _h: number): void {
      /* no-op */
    },
    dispose(): void {
      /* no-op */
    },
  };
}

export interface AcquiredRenderer {
  readonly renderer: Renderer;
  /** True when the real Render module was used (false = null fallback). */
  readonly real: boolean;
}

export function acquireRenderer(canvas: HTMLCanvasElement): AcquiredRenderer {
  try {
    return { renderer: createRenderer(canvas), real: true };
  } catch {
    return { renderer: createNullRenderer(), real: false };
  }
}
